// Pure helpers for the player's home hero on joulelingo.com (PlayerHomeHero.jsx). Explicit
// extensions so this runs under plain `node --test` as well as through Vite.
import { PLAYER_ROLES, QUIZ_TEACHER_ROLES } from '../../../lib/roles.js'
import { hasRole } from '../../../utils/hasRole.js'

/**
 * Whether the home should greet `user` as a player: they hold the Student Role and no
 * staff-facing role. Staff who also play keep the calm home.
 */
export function isPlayer(user) {
    if (!user || !Array.isArray(user.roles)) return false
    return hasRole(user, PLAYER_ROLES) && !hasRole(user, QUIZ_TEACHER_ROLES)
}

/**
 * What the hero says about this week's challenge (GET /api/quiz/challenge/), as
 * `{ status, line, action }`. A missing status reads as not started, the same default the
 * challenge card uses.
 */
export function challengeCall(challenge) {
    const status = challenge?.status ?? 'not_started'
    if (status === 'completed') {
        const scored =
            typeof challenge?.score === 'number' &&
            typeof challenge?.total === 'number'
        return {
            status,
            line: scored
                ? `Done! You scored ${challenge.score} / ${challenge.total} this week.`
                : 'Done for this week.',
            action: 'See the leaderboard'
        }
    }
    if (status === 'in_progress') {
        const answered = challenge?.answered ?? 0
        return {
            status,
            line: `You’ve answered ${answered} so far: pick up where you left off.`,
            action: 'Carry on'
        }
    }
    return {
        status: 'not_started',
        line: 'This week’s challenge is ready for you.',
        action: 'Start this week’s challenge'
    }
}

/** 1 → "1st", 2 → "2nd", 11 → "11th", 22 → "22nd". */
export function ordinal(n) {
    const tens = n % 100
    if (tens >= 11 && tens <= 13) return `${n}th`
    const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'
    return `${n}${suffix}`
}

/**
 * The reader's place in a built league (houseLeague.js buildLeagueRows rows), or null when
 * they have none there or it has no rank yet (nobody played). `{ row, rank, of, joint }`:
 * `of` counts the ranked rows, `joint` is true when another row shares the rank.
 */
export function myLeaguePlace(rows) {
    const list = rows ?? []
    const row = list.find((r) => r.isMine)
    if (!row || typeof row.rank !== 'number') return null
    const ranked = list.filter((r) => typeof r.rank === 'number')
    return {
        row,
        rank: row.rank,
        of: ranked.length,
        joint: ranked.some((r) => r !== row && r.rank === row.rank)
    }
}

/** "2nd of 4", "Joint 1st of 4". */
export function placeLabel(place) {
    const position = ordinal(place.rank)
    return `${place.joint ? `Joint ${position}` : position} of ${place.of}`
}

// The week's comparison only moves the character when it is a fair one. Below NOTICEABLE
// percent it is "about the same"; beyond BIG_SWING percent the two weeks were not alike (a
// holiday on one side, most likely), so the character states the fact and neither cheers nor
// nudges.
const NOTICEABLE = 5
const BIG_SWING = 40
// Readings arrive half-hourly; the last slot of the window's final day starts at 23:30. A
// week whose last reading is earlier than this before the window's end is still filling in,
// and a part-filled week would read as a drop worth cheering.
const COMPLETE_WITHIN_MS = 60 * 60 * 1000

/**
 * How the character on the player's home reacts to the week's energy, from the power
 * overview payload for `last_7_days` (the same request the home's energy card makes):
 * `{ mood, trend, line }`, or null when there is nothing fair to say (no readings, no previous
 * week, or this week still filling in). Then the character just waves hello.
 *
 * It never scolds: using more gets an invitation to help, not a sad face. 'Worried' is kept for
 * a spike nobody expected, which a week-on-week total cannot tell apart from a holiday ending,
 * so it comes from `spike` instead (GET /api/alerts/spike-now/: the staff spike alert, which
 * compares a day with the same weekday's usual and skips holidays). A spike outranks the
 * week, so the character never cheers while one is open.
 */
export function energyMood(overview, { spike = false } = {}) {
    if (spike === true) {
        return {
            mood: 'worried',
            trend: 'spike',
            line: 'Uh oh, we’re using a lot more energy than usual. Can you help find what’s been left on?'
        }
    }
    const comparison = overview?.previousPeriodComparison
    const freshness = overview?.freshness?.electricity
    if (!comparison || !(comparison.previousPeriod > 0)) return null
    if (freshness && freshness.hasReadingsInPeriod === false) return null
    const lastReading = Date.parse(freshness?.lastReadingAt ?? '')
    const windowEnd = Date.parse(overview?.windowEnd ?? '')
    if (
        !Number.isFinite(lastReading) ||
        !Number.isFinite(windowEnd) ||
        lastReading < windowEnd - COMPLETE_WITHIN_MS
    ) {
        return null
    }

    const change = Number(comparison.percentageChange)
    if (!Number.isFinite(change)) return null
    const size = Math.round(Math.abs(change))
    const direction = change < 0 ? 'less' : 'more'

    if (size < NOTICEABLE) {
        return {
            mood: 'happy',
            trend: 'same',
            line: 'We used about the same energy as last week.'
        }
    }
    if (size > BIG_SWING) {
        return {
            mood: 'happy',
            trend: 'swing',
            line: `We used ${size}% ${direction} energy than last week.`
        }
    }
    if (direction === 'less') {
        return {
            mood: 'celebrating',
            trend: 'less',
            line: `We used ${size}% less energy than last week. Brilliant!`
        }
    }
    return {
        mood: 'happy',
        trend: 'more',
        line: `We used ${size}% more energy than last week. Can you spot something to switch off?`
    }
}

// Monday's recap of last week's goal (the team goal's `lastWeek`), as a trend for tests and a
// line for the panel. Never a failure: a week short of the goal is just a fresh start.
function lastWeekTrend(lastWeek) {
    const percent = Number(lastWeek?.percentVsUsual)
    if (!Number.isFinite(percent)) return null
    if (lastWeek.reached === true) return 'reached'
    if (percent < 0) return 'closer'
    if (percent === 0) return 'level'
    return 'behind'
}

function lastWeekLine(lastWeek) {
    const trend = lastWeekTrend(lastWeek)
    const percent = Number(lastWeek?.percentVsUsual)
    const size = Math.abs(percent)
    if (trend === 'reached') {
        return `Last week we used ${size}% less energy than usual. Goal reached!`
    }
    if (trend === 'closer') {
        return `Last week we used ${size}% less energy than usual. Let’s go further this week!`
    }
    if (trend === 'level') {
        return 'Last week we used about the same energy as usual. New week, fresh start!'
    }
    if (trend === 'behind') {
        return `Last week we used ${size}% more energy than usual. New week, fresh start!`
    }
    return 'A new week has started. We’ll see how we’re doing tomorrow.'
}

/**
 * What the team goal panel says, from GET /api/power/team-goal/: `{ state, goal, line,
 * progress, reached }`, or null when there is no fair goal this week (a holiday, too few usual
 * weeks to compare with, or no readings). `progress` runs 0 to 1 towards the target saving.
 *
 * It is everyone's goal, so it never singles anyone out, and like the character it never
 * scolds: using more than usual is "still time", not a failure.
 */
export function teamGoalCall(teamGoal) {
    const target = teamGoal?.targetPercent
    if (!(target > 0)) return null
    const goal = `Use ${target}% less energy than usual this week`
    if (teamGoal.state === 'starting') {
        return {
            state: 'starting',
            goal,
            line: lastWeekLine(teamGoal.lastWeek),
            progress: 0,
            reached: false,
            lastWeek: lastWeekTrend(teamGoal.lastWeek)
        }
    }
    const percent = Number(teamGoal.percentVsUsual)
    if (teamGoal.state !== 'active' || !Number.isFinite(percent)) return null

    const saved = Math.max(0, -percent)
    if (saved >= target) {
        return {
            state: 'reached',
            goal,
            line: `We’re using ${saved}% less energy than usual. Goal reached so far!`,
            progress: 1,
            reached: true
        }
    }
    const progress = saved / target
    if (percent < 0) {
        return {
            state: 'closer',
            goal,
            line: `We’re using ${saved}% less energy than usual. ${target - saved}% to go!`,
            progress,
            reached: false
        }
    }
    if (percent === 0) {
        return {
            state: 'level',
            goal,
            line: 'We’re using about the same energy as usual so far. Let’s find something to switch off!',
            progress,
            reached: false
        }
    }
    return {
        state: 'behind',
        goal,
        line: `We’re using ${percent}% more energy than usual so far. There’s still time to turn it round!`,
        progress,
        reached: false
    }
}
